import { useEffect, useRef, useSyncExternalStore, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface DialogKeyHandlers {
  onEscape: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
}

/**
 * Comportement clavier d'une boîte de dialogue plein écran (modale d'arrivée,
 * visionneuse), tant que le composant est monté :
 * - le focus entre dans la boîte, y reste piégé (Tab / Maj+Tab bouclent), et
 *   revient à l'élément qui l'avait à la fermeture ;
 * - Échap ferme, les flèches gauche/droite naviguent ;
 * - le défilement de la page derrière est bloqué.
 *
 * Effet pur de synchronisation avec le DOM : il ne dépend que du montage, les
 * gestionnaires sont lus à travers une référence toujours à jour.
 */
export function useDialogA11y(containerRef: RefObject<HTMLElement>, handlers: DialogKeyHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    container.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handlersRef.current.onEscape();
        return;
      }
      if (event.key === 'ArrowLeft' && handlersRef.current.onArrowLeft) {
        event.preventDefault();
        handlersRef.current.onArrowLeft();
        return;
      }
      if (event.key === 'ArrowRight' && handlersRef.current.onArrowRight) {
        event.preventDefault();
        handlersRef.current.onArrowRight();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && container.contains(active);

      if (event.shiftKey) {
        if (!inside || active === first || active === container) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Phase de capture sur le document : la boîte reçoit les touches même si le
    // focus lui a échappé (clic sur le fond, par exemple).
    document.addEventListener('keydown', onKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      // L'élément d'origine peut avoir disparu (navigation) : rien à restaurer alors.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [containerRef]);
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

/** L'utilisateur a-t-il demandé à son système de réduire les animations ? */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );
}
